import { createId } from '@paralleldrive/cuid2'
import type { HarmonyPackageDocument } from '@rhythmic/db/types'
import type { Collection, Transaction } from '@tanstack/vue-db'
import { useRhythmicLogger } from '#imports'
import { getHarmonyDocumentCollection } from '../getCollections'

const logger = useRhythmicLogger('useHarmonyPlanContext:createDocument')

export interface CreateHarmonyPlanDocumentPayload {
  name: string
  image?: string | null
  context?: string | null
  markdown?: string | null
  publishStatus?: string | null
  ragSummary?: string | null
  ragOneLiner?: string | null
  folderId?: string | null
}

export interface HarmonyPlanDocumentMutationContext {
  planId: string
  teamId: string
  workspaceId: string
  collection?: Collection<HarmonyPackageDocument>
}

function validateParams(_payload: CreateHarmonyPlanDocumentPayload, context: HarmonyPlanDocumentMutationContext) {
  logger.debug('Validating parameters for creating a harmony plan document', {
    context,
    payload: _payload,
  })
  const { planId, workspaceId } = context

  if (!planId) throw new Error('Harmony plan identifier is required to create a document')

  if (!workspaceId) throw new Error('Workspace identifier is required to create a harmony plan document')

  return { workspaceId, planId }
}

export function handleCreateHarmonyPlanDocument(
  payload: CreateHarmonyPlanDocumentPayload,
  context: HarmonyPlanDocumentMutationContext,
): Transaction {
  try {
    logger.debug('Creating a new harmony plan document', { context, payload })
    const { workspaceId, planId } = validateParams(payload, context)
    const collection = getHarmonyDocumentCollection(planId)
    const insertPayload: HarmonyPackageDocument = {
      id: `document_${createId()}`,
      name: payload.name || 'Untitled Document',
      image: payload.image ?? null,
      context: payload.context ?? null,
      publish_status: 'draft',
      created_at: new Date(),
      updated_at: new Date(),
      last_indexed_at: null,
      package_id: planId,
      workspace_id: workspaceId,
      rag_summary: payload.ragSummary ?? null,
      rag_one_liner: payload.ragOneLiner ?? null,
      markdown: payload.markdown ?? null,
      folder_id: payload.folderId ?? null,
    }
    logger.debug('Inserting harmony plan document into collection', {
      insertPayload,
    })
    return collection.insert(insertPayload, { optimistic: false })
  } catch (error) {
    logger.error('Failed to create harmony plan document', { error })
    if (error instanceof Error) throw error

    throw new Error('Failed to create harmony plan document', { cause: error })
  }
}
